import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  DiagnosisCodesRepositoryService,
  Events,
  PatientDiagnosisCode,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';

/**
 * PatientDiagnosisCodesStore — ICD-10 diagnosis codes for the current patient.
 *
 * Same shape as PatientProblemsStore: effect-driven auto-load, owns the Pusher
 * subscription for DiagnosisCodeDiagnosisCodeChanged, exposes optimistic
 * add/remove with rollback.
 *
 * Unlike the problem list there is no "create a new master item" path — ICD-10
 * codes are fixed master data, so the only way in is picking an existing code
 * out of master data search.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientDiagnosisCodesStore {
  private patientStore = inject(PatientStore);
  private repo = inject(DiagnosisCodesRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<PatientDiagnosisCode[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.DiagnosisCodeDiagnosisCodeChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Add a diagnosis code to the current patient. Optimistic with rollback.
   *
   * `masterCodeId` is the id of the row the user picked out of master data
   * search. That is what the API wants — it is NOT `item.id`, which is the
   * patient-link row id and is only known once the server responds.
   */
  add(item: PatientDiagnosisCode, masterCodeId: number): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientDiagnosisCodesStore.add() called with no current patient');
    }
    const patientId = patient.id;
    const previous = this._items();
    this._items.set([...previous, item]);

    const request = {
      id: masterCodeId,
      icd10Code: item.icD10Code,
      comment: item.comment
    };

    return this.repo.addDiagnosisCodeToPatient(patientId, request).pipe(
      tap({
        next: newId => {
          item.id = newId;
          this._items.set([...this._items()]);
          this.reload();
        },
        error: () => this.rollback(previous, patientId)
      })
    );
  }

  /**
   * Remove a diagnosis code from the current patient. Optimistic with rollback.
   */
  remove(diagnosisCodeId: number): Observable<boolean> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientDiagnosisCodesStore.remove() called with no current patient');
    }
    const patientId = patient.id;
    const previous = this._items();
    this._items.set(previous.filter(code => code.id !== diagnosisCodeId));
    return this.repo.removeDiagnosisCodeFromPatient(patientId, diagnosisCodeId).pipe(
      tap({
        // The repository turns a non-OK envelope into `false` rather than
        // erroring, so a "successful" response can still mean the server kept
        // the row — roll back on that too, not just on a transport error.
        next: deleted => {
          if (!deleted) {
            this.rollback(previous, patientId);
          }
        },
        error: () => this.rollback(previous, patientId)
      })
    );
  }

  /**
   * Restore a pre-mutation snapshot after a failed write, but only if the
   * store is still showing the patient that write was issued for. Without the
   * check, a request that fails after the user has navigated to another
   * patient would put the previous patient's codes on screen.
   */
  private rollback(previous: PatientDiagnosisCode[], patientId: number): void {
    if (this.patientStore.current()?.id !== patientId) return;
    this._items.set(previous);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientDiagnosisCodes(patientId).subscribe({
      next: codes => {
        const sorted = [...codes].sort((a, b) =>
          a.createdDate > b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: () => {
        this._loading.set(false);
        this._error.set('Could not load patient diagnosis codes');
      }
    });
  }
}
